/* eslint-disable arrow-body-style */

import http from '@gp/utils/httpTS';

import type { IBaseResponse } from '@home/types/common';
import type { IMilestoneSimpleDTO } from '@gp/types/global';
import type {
  IDPSReportType,
  IDPSReportCalendarProjectSimpleVO,
  IDPSReportCalendarDTO,
} from '@gp/types/dpsReport/common';
import type {
  IDPSReportUpdatesOfTheWeekChangeDTO,
  IDPSReportDTOV3,
  IDPSReportEditParamsV3,
  IDPSReportUpdatesOfTheWeekDTO,
  IDPSReportMemoPendingApprovalMemoInfoDTO,
} from '@gp/types/dpsReport/v3';

export interface ICommonParams {
  project_id: string;
  start_date: string;
  end_date: string;
}

/**
 * 获取指定DPS周报详情
 * @param params 参数对象
 * @param params.report_id DPS周报 id
 * @returns 指定DPS周报详情
 */
export const fetchDPSReportDetails = async (params: {
  type: IDPSReportType;
  id: string;
  report_id: string;
  start_date: string;
  end_date: string;
}): IBaseResponse<IDPSReportDTOV3 | null> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/weekly_report_v3',
    method: 'POST',
    data: params,
    rawData: true,
  }) as any;
};

/**
 * 获取dps周报新增、编辑时，可选的全量project集合
 * @returns 全量project集合
 */
export const fetchFullyProjects = async (): IBaseResponse<{
  list: IDPSReportCalendarProjectSimpleVO[] | null;
}> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/project_list_v3',
    method: 'GET',
    params: {},
    rawData: true,
  }) as any;
};

/**
 * 获取指定project的变动记录（Updates of the Week模块）
 * @param params 参数对象
 * @param params.report_id report id
 * @param params.project_id project id
 * @param params.start_date report start date
 * @param params.end_date report end date
 * @returns 指定project的变动记录（Updates of the Week模块）
 */
export const fetchUpdatesOfWeekChanges = async (
  params: ICommonParams,
): IBaseResponse<{ list: IDPSReportUpdatesOfTheWeekChangeDTO[] | null; }> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/updates_of_week_add_v3',
    method: 'GET',
    params,
    rawData: true,
  }) as any;
};

/**
 * 获取 指定project的memo附件（Memo Pending Approval模块）
 * @param params 参数对象
 * @returns 指定project的memo附件（Memo Pending Approval模块）
 */
export const fetchMemoMaterials = async (
  params: ICommonParams,
): IBaseResponse<IDPSReportMemoPendingApprovalMemoInfoDTO> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/milestone_memo_add_v3',
    method: 'GET',
    params,
    rawData: true,
  }) as any;
};

/**
 * 获取指定project的milestones集合（Calendar模块）
 * @param params 参数对象
 * @param params.project_id project id
 * @returns 指定project的milestones集合（Calendar模块）
 */
export const fetchCalendarMilestones = async (params: {
  project_id: string[];
  start_date: string;
  end_date: string;
}): IBaseResponse<{ list: IMilestoneSimpleDTO[] | null; }> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/calendar_add_v3',
    method: 'POST',
    data: params,
    rawData: true,
  }) as any;
};

/**
 * 获取 新增DPS周报时，Updates of the Week模块初始化数据（系统自动拉取）
 * @param params 参数对象
 * @returns 新增DPS周报时，Updates of the Week模块初始化数据（系统自动拉取）
 */
export const fetchUpdatesOfWeeks = async (
  params: Omit<ICommonParams, 'project_id'>,
): IBaseResponse<{ list: IDPSReportUpdatesOfTheWeekDTO[] | null; }> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/updates_of_week_init_v3',
    method: 'GET',
    params,
    rawData: true,
  }) as any;
};

/**
 * 获取 新增DPS周报时，Calendar模块初始化数据（系统自动拉取）
 * @param params 参数对象
 * @returns 新增DPS周报时，Calendar模块初始化数据（系统自动拉取）
 */
export const fetchCalendars = async (
  params: Omit<ICommonParams, 'project_id'>,
): IBaseResponse<{ list: IDPSReportCalendarDTO[] | null; }> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/calendar_init_v3',
    method: 'GET',
    params,
    rawData: true,
  }) as any;
};

/**
 * 编辑DPS周报，新增、编辑同一个接口
 */
export const editDPSReport = (params: IDPSReportEditParamsV3): IBaseResponse<any> => {
  return http({
    url: '/api/v1/globalpedia_pc/dps_report/edit_report_v3',
    method: 'POST',
    data: params,
    rawData: true,
  }) as any;
};
